<template>
  <PForm nested :schema="r$" :validate-on="[]" class="availability-form-daily-item">
    <div class="flex items-center gap-x-2">
      <div class="grid min-w-0 flex-1 grid-cols-5 items-center gap-x-2">
        <!-- Day label -->
        <span v-if="index === 0" class="col-span-2 text-sm font-semibold select-none">
          {{ recurringAvailability.getDayOfWeekDisplay() }}
        </span>

        <!-- Time slot -->
        <div
          class="col-span-3 grid min-w-0 grid-cols-12 gap-x-2"
          :class="{ 'col-start-3': index > 0 }"
        >
          <div class="col-span-12 flex items-center gap-x-2">
            <span v-if="!isAvailableOnThisDay" class="text-sm select-none"> Niet beschikbaar </span>
            <PFormField
              v-if="isAvailableOnThisDay"
              :label="`${recurringAvailability.getDayOfWeekDisplay()}: starttijd`"
              class="min-w-0 flex-1"
              sr-only
            >
              <PInputTime
                v-model="recurringAvailability.timeStart"
                :disabled="
                  recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE || disabled
                "
                time-only
                @focusout="onFocusOut"
              />
            </PFormField>
            <PFormField
              v-if="isAvailableOnThisDay"
              :label="`${recurringAvailability.getDayOfWeekDisplay()}: eindtijd`"
              class="min-w-0 flex-1"
              sr-only
            >
              <PInputTime
                v-model="recurringAvailability.timeEnd"
                :disabled="
                  recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE || disabled
                "
                time-only
                @focusout="onFocusOut"
              />
            </PFormField>
          </div>
          <div v-if="errorMessage" class="col-span-12">
            <span class="text-error text-sm select-none">
              {{ errorMessage }}
            </span>
          </div>
        </div>
      </div>

      <!-- Keep actions outside the time fields so their widths stay aligned across days. -->
      <div class="grid w-16 shrink-0 grid-cols-3 items-center gap-x-2">
        <PButton
          v-if="isLast && recurringAvailability.isComplete()"
          variant="ghost"
          :icon="['fas', 'plus']"
          :disabled="
            recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE ||
            disabled ||
            !canAdd
          "
          title="Tijdslot toevoegen"
          class="row-start-1 justify-self-start p-0!"
          :class="isFirst ? 'col-start-1' : 'col-start-2'"
          @click="$emit('addRecurringAvailability', recurringAvailability)"
        />
        <PButton
          v-if="!isLast || !isFirst"
          variant="ghost"
          :icon="['fas', 'minus']"
          :disabled="
            recurringAvailability.availabilityType !== AvailabilityTypes.AVAILABLE ||
            disabled ||
            isBusy
          "
          title="Tijdslot verwijderen"
          class="col-start-1 row-start-1 justify-self-start p-0!"
          @click="onClickRemove"
        />
        <PSwitch
          v-if="isFirst"
          class="col-span-2 col-start-2 row-start-1 justify-self-end"
          v-model="isAvailableOnThisDay"
          :title="`${recurringAvailability.getDayOfWeekDisplay()} beschikbaar`"
          :disabled="disabled"
          @update:model-value="onUpdateDayAvailability"
        />
      </div>
    </div>
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import { computed, inject, ref } from "vue"
import { useRegle, markStatic, inferRules } from "@regle/core"
import PButton from "@/components/elements/PButton.vue"
import PSwitch from "@/components/form/PSwitch.vue"
import PInputTime from "@/components/form/PInputTime.vue"
import PFormField from "@/components/form/PFormField.vue"
import { RecurringAvailability } from "@/models/recurring-availability.model.ts"
import { AvailabilityTypes } from "@/enums/models/availability-types.ts"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import {
  chronologicalStartAndEndTimeValidator,
  timeNoOverlapValidator
} from "@/helper/validation/recurring-availability-validation.ts"
import type { RecurringAvailabilityController } from "@/controllers/model-controllers/recurring-availability-controller.ts"
import useEntityUpdater from "@/composables/api/entity-updater.ts"

const recurringAvailability = defineModel<RecurringAvailability>({ required: true })

const isAvailableOnThisDay = ref(
  recurringAvailability.value.availabilityType === AvailabilityTypes.AVAILABLE
)

const recurringAvailabilityController = inject<RecurringAvailabilityController>(
  "recurringAvailabilityController"
)!
const entityUpdater = useEntityUpdater(
  recurringAvailability,
  recurringAvailabilityController.update,
  recurringAvailabilityController.create
)

function updateIfValid() {
  if (!r$.$invalid) {
    entityUpdater.update()
  }
}
const delayedUpdate = useDelayedFunction(updateIfValid, 300)
const { isBusy } = entityUpdater

const props = defineProps<{
  index: number
  isFirst: boolean
  isLast: boolean
  canAdd: boolean
  disabled: boolean
  allRecurringAvailabilities: RecurringAvailability[]
}>()

const emit = defineEmits(["addRecurringAvailability", "removeRecurringAvailability"])

function onClickRemove() {
  if (isBusy.value) {
    return
  }

  delayedUpdate.cancel()

  if (recurringAvailability.value.isSaved) {
    // Removing saved by controller will update parent collection automatically.
    recurringAvailabilityController.deleteOrRollback(recurringAvailability.value)
  } else {
    // Local (unsaved) entity. Tell the parent to remove it.
    emit("removeRecurringAvailability", recurringAvailability.value)
  }

  // Re-validate after DOM has updated, or validation errors on the deleted will not be cleared.
  setTimeout(r$.$touch)
}

async function onUpdateDayAvailability(isAvailable: boolean) {
  if (isAvailable) {
    // Update existing recurring availability to available
    recurringAvailability.value.availabilityType = AvailabilityTypes.AVAILABLE
    await recurringAvailabilityController.update(recurringAvailability.value)
  } else {
    // Delete all recurring availabilities for this day except one
    const [remaining, ...toDelete] = props.allRecurringAvailabilities

    if (toDelete.length > 0) {
      await recurringAvailabilityController.bulkDelete(toDelete)
    }

    // Update the remaining recurring availability to unavailable
    remaining.availabilityType = AvailabilityTypes.UNAVAILABLE
    remaining.timeStart = null
    remaining.timeEnd = null
    await entityUpdater.update(remaining)
  }
}

function onFocusOut() {
  r$.$touch()
  delayedUpdate.schedule()
}

// --- Validation ---

const validationRules = computed(() =>
  inferRules(validationState, {
    range: { timeRange: chronologicalStartAndEndTimeValidator() },
    overlap: {
      noOverlap: timeNoOverlapValidator(
        props.allRecurringAvailabilities,
        recurringAvailability.value
      )
    }
  })
)

const validationState = computed(() => ({
  range: markStatic({
    timeStart: recurringAvailability.value.timeStart,
    timeEnd: recurringAvailability.value.timeEnd
  }),
  overlap: markStatic({ ...recurringAvailability.value })
}))

const { r$ } = useRegle(validationState, validationRules, { autoDirty: false })

const errorMessage = computed(() => {
  if (
    props.disabled ||
    recurringAvailability.value.availabilityType !== AvailabilityTypes.AVAILABLE
  ) {
    return null
  }

  return r$.range?.$errors?.[0] || r$.overlap?.$errors?.[0] || null
})
</script>
